import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import LineChart from '../components/LineChart'
import './Progreso.css'

const PARTES_CUERPO = ['cintura', 'cadera', 'pecho', 'brazo', 'muslo', 'pantorrilla', 'cuello']
const ETIQUETA_PARTE = {
  cintura: 'Cintura', cadera: 'Cadera', pecho: 'Pecho', brazo: 'Brazo',
  muslo: 'Muslo', pantorrilla: 'Pantorrilla', cuello: 'Cuello'
}
const RANGO_PESO = [20, 250]
// Rangos generosos a propósito: el objetivo es atajar errores de tipeo (ej. "900"),
// no poner un techo clínico — no deben bloquear a pacientes con cuerpos reales fuera
// del promedio.
const RANGO_MEDIDA = {
  cintura: [40, 200], cadera: [50, 200], pecho: [50, 200], brazo: [15, 90],
  muslo: [25, 190], pantorrilla: [15, 90], cuello: [20, 80], otro: [1, 300]
}
const MAX_FOTOS = 4

function formatearFecha(fechaStr) {
  const d = new Date(fechaStr + 'T00:00:00')
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })
}

// fecha sola no alcanza para ordenar de forma determinística: dos registros del
// mismo día (ej. paciente que carga dos veces en la misma fecha) no tienen orden
// garantizado en Postgres sin una clave secundaria — created_at la resuelve.
function ordenarPorFechaYHora(a, b) {
  return a.fecha.localeCompare(b.fecha) || a.created_at.localeCompare(b.created_at)
}

// Dirección y progreso se calculan siempre contra el primer registro histórico de
// peso del paciente — no contra un valor propio del objetivo — para que "cambio
// total" (gráfico) y el avance del objetivo queden ancladas al mismo punto de partida.
function direccionObjetivo(pesoObjetivo, pesoPrimero) {
  return pesoObjetivo < pesoPrimero ? 'bajar' : 'subir'
}

function objetivoSatisfecho(pesoObjetivo, pesoPrimero, nuevoPeso) {
  return direccionObjetivo(pesoObjetivo, pesoPrimero) === 'bajar' ? nuevoPeso <= pesoObjetivo : nuevoPeso >= pesoObjetivo
}

export default function Progreso({ usuario, abrirRegistroTrigger }) {
  const [registros, setRegistros] = useState([])
  const [medidasPorRegistro, setMedidasPorRegistro] = useState({})
  const [fotosPorRegistro, setFotosPorRegistro] = useState({})
  const [objetivos, setObjetivos] = useState([])
  const [loading, setLoading] = useState(true)
  const [pantallaObjetivo, setPantallaObjetivo] = useState(false)
  const [modalRegistro, setModalRegistro] = useState(false)
  const [subiendo, setSubiendo] = useState(false)
  const [parteSeleccionada, setParteSeleccionada] = useState('cintura')
  const [erroresForm, setErroresForm] = useState({})

  const [formObj, setFormObj] = useState({ peso_meta: '' })
  const [formReg, setFormReg] = useState({
    fecha: new Date().toISOString().split('T')[0],
    peso: '',
    medidas: { cintura: '', cadera: '', pecho: '', brazo: '', muslo: '', pantorrilla: '', cuello: '' },
    otroNombre: '', otroValor: '',
    notas: ''
  })
  const inputFotoRef = useRef(null)
  const [fotosSeleccionadas, setFotosSeleccionadas] = useState([])

  useEffect(() => { cargarDatos() }, [])

  // Bloque 3.3, Fase D: el hub de Configuración manda al paciente acá para
  // cargar su primer registro — reusa este mismo modal, no un formulario
  // nuevo. abrirRegistroTrigger arranca en 0 (falsy) así que una navegación
  // normal a esta tab no dispara nada; solo cambia cuando el hub lo pide.
  useEffect(() => {
    if (abrirRegistroTrigger) { setErroresForm({}); setModalRegistro(true) }
  }, [abrirRegistroTrigger])

  const cargarDatos = async () => {
    setLoading(true)
    const [{ data: regs }, { data: objs }] = await Promise.all([
      supabase.from('registros_progreso').select('*').eq('usuario_id', usuario.id).order('fecha', { ascending: true }).order('created_at', { ascending: true }),
      supabase.from('objetivos_peso').select('*').eq('usuario_id', usuario.id).order('orden', { ascending: true })
    ])

    const idsMedida = (regs || []).filter(r => r.tipo === 'medida').map(r => r.id)
    const idsFoto = (regs || []).filter(r => r.tipo === 'foto').map(r => r.id)

    const [{ data: medidas }, { data: fotos }] = await Promise.all([
      idsMedida.length ? supabase.from('registro_medidas').select('*').in('registro_id', idsMedida) : Promise.resolve({ data: [] }),
      idsFoto.length ? supabase.from('registro_fotos').select('*').in('registro_id', idsFoto).order('orden') : Promise.resolve({ data: [] })
    ])

    const mMedidas = {}
    for (const m of medidas || []) (mMedidas[m.registro_id] ??= []).push(m)
    const mFotos = {}
    for (const f of fotos || []) (mFotos[f.registro_id] ??= []).push(f)

    setRegistros(regs || [])
    setMedidasPorRegistro(mMedidas)
    setFotosPorRegistro(mFotos)
    setObjetivos(objs || [])
    setLoading(false)
  }

  const validarPeso = (valor) => {
    const n = parseFloat(valor)
    if (isNaN(n)) return null
    if (n < RANGO_PESO[0] || n > RANGO_PESO[1]) return `El peso debe estar entre ${RANGO_PESO[0]} y ${RANGO_PESO[1]} kg.`
    return null
  }

  const validarMedida = (parte, valor) => {
    const n = parseFloat(valor)
    if (isNaN(n)) return null
    const [min, max] = RANGO_MEDIDA[parte] || [1, 300]
    if (n < min || n > max) return `${ETIQUETA_PARTE[parte] || 'Esa medida'} debe estar entre ${min} y ${max} cm.`
    return null
  }

  const guardarObjetivo = async () => {
    const errores = {}
    const errPesoMeta = validarPeso(formObj.peso_meta)
    if (errPesoMeta) errores.peso_meta = errPesoMeta
    if (!formObj.peso_meta) errores.general = 'Completá el peso meta.'
    if (Object.keys(errores).length) { setErroresForm(errores); return }

    const po = parseFloat(formObj.peso_meta)
    const siguienteOrden = objetivos.length ? Math.max(...objetivos.map(o => o.orden)) + 1 : 1

    const { data, error } = await supabase.from('objetivos_peso').insert({
      usuario_id: usuario.id, peso_objetivo: po, orden: siguienteOrden
    }).select().single()

    if (!error && data) {
      setObjetivos(prev => [...prev, data])
      setErroresForm({})
      setPantallaObjetivo(false)
    }
  }

  const guardarRegistro = async () => {
    const errores = {}
    if (formReg.peso) {
      const e = validarPeso(formReg.peso)
      if (e) errores.peso = e
    }
    for (const parte of PARTES_CUERPO) {
      if (formReg.medidas[parte]) {
        const e = validarMedida(parte, formReg.medidas[parte])
        if (e) errores[`medida_${parte}`] = e
      }
    }
    if (formReg.otroValor) {
      const e = validarMedida('otro', formReg.otroValor)
      if (e) errores.otro = e
      if (!formReg.otroNombre.trim()) errores.otroNombre = 'Indicá qué parte es.'
    }
    if (Object.keys(errores).length) { setErroresForm(errores); return }

    const hayPeso = !!formReg.peso
    const medidasCompletas = PARTES_CUERPO.filter(p => formReg.medidas[p])
    const hayOtro = !!formReg.otroValor
    const hayMedidas = medidasCompletas.length > 0 || hayOtro
    const hayFotos = fotosSeleccionadas.length > 0

    if (!hayPeso && !hayMedidas && !hayFotos) {
      setErroresForm({ general: 'Cargá al menos un dato (peso, medida o foto).' })
      return
    }

    setSubiendo(true)
    const nuevosRegistros = []
    const nuevasMedidasPorId = {}
    const nuevasFotosPorId = {}

    if (hayPeso) {
      const { data } = await supabase.from('registros_progreso').insert({
        usuario_id: usuario.id, fecha: formReg.fecha, tipo: 'peso',
        peso: parseFloat(formReg.peso), notas: formReg.notas || null
      }).select().single()
      if (data) {
        nuevosRegistros.push(data)
        // objetivo activo = el último creado sin alcanzar (no el primero — "cambiar
        // objetivo" agrega un step nuevo, y ese debe pasar a ser el vigente).
        const objetivoActivo = objetivos.filter(o => !o.alcanzado).at(-1)
        // ancla = primer peso histórico ya cargado, o este mismo si es el primero de todos.
        const anclaPeso = registros.filter(r => r.tipo === 'peso' && r.peso != null)[0]?.peso ?? data.peso
        if (objetivoActivo && objetivoSatisfecho(objetivoActivo.peso_objetivo, anclaPeso, data.peso)) {
          const { data: actualizado } = await supabase.from('objetivos_peso')
            .update({ alcanzado: true, fecha_alcanzado: data.fecha })
            .eq('id', objetivoActivo.id).select().single()
          if (actualizado) setObjetivos(prev => prev.map(o => o.id === actualizado.id ? actualizado : o))
        }
      }
    }

    if (hayMedidas) {
      const { data: header } = await supabase.from('registros_progreso').insert({
        usuario_id: usuario.id, fecha: formReg.fecha, tipo: 'medida', notas: formReg.notas || null
      }).select().single()
      if (header) {
        nuevosRegistros.push(header)
        const filas = medidasCompletas.map(parte => ({
          registro_id: header.id, parte_cuerpo: parte, medida_cm: parseFloat(formReg.medidas[parte])
        }))
        if (hayOtro) filas.push({
          registro_id: header.id, parte_cuerpo: 'otro', parte_cuerpo_otro: formReg.otroNombre.trim(),
          medida_cm: parseFloat(formReg.otroValor)
        })
        const { data: insertadas } = await supabase.from('registro_medidas').insert(filas).select()
        nuevasMedidasPorId[header.id] = insertadas || []
      }
    }

    if (hayFotos) {
      const { data: header } = await supabase.from('registros_progreso').insert({
        usuario_id: usuario.id, fecha: formReg.fecha, tipo: 'foto', notas: formReg.notas || null
      }).select().single()
      if (header) {
        nuevosRegistros.push(header)
        const filas = []
        for (let i = 0; i < fotosSeleccionadas.length; i++) {
          const file = fotosSeleccionadas[i]
          const ext = file.name.split('.').pop()
          const path = `${usuario.id}/${Date.now()}-${i}.${ext}`
          await supabase.storage.from('fotos-progreso').upload(path, file)
          const { data: { publicUrl } } = supabase.storage.from('fotos-progreso').getPublicUrl(path)
          filas.push({ registro_id: header.id, foto_url: publicUrl, orden: i + 1 })
        }
        const { data: insertadas } = await supabase.from('registro_fotos').insert(filas).select()
        nuevasFotosPorId[header.id] = insertadas || []
      }
    }

    setRegistros(prev => [...prev, ...nuevosRegistros].sort(ordenarPorFechaYHora))
    setMedidasPorRegistro(prev => ({ ...prev, ...nuevasMedidasPorId }))
    setFotosPorRegistro(prev => ({ ...prev, ...nuevasFotosPorId }))

    setFormReg({
      fecha: new Date().toISOString().split('T')[0], peso: '',
      medidas: { cintura: '', cadera: '', pecho: '', brazo: '', muslo: '', pantorrilla: '', cuello: '' },
      otroNombre: '', otroValor: '', notas: ''
    })
    setFotosSeleccionadas([])
    setErroresForm({})
    setModalRegistro(false)
    setSubiendo(false)
  }

  if (loading) return <div className="loading">Cargando...</div>

  const registrosPeso = registros.filter(r => r.tipo === 'peso' && r.peso != null)
  const pesoActual = registrosPeso.at(-1)?.peso
  const pesoPrimero = registrosPeso[0]?.peso
  const acumuladoPeso = (pesoActual != null && pesoPrimero != null && registrosPeso.length > 1)
    ? +(pesoActual - pesoPrimero).toFixed(1) : null

  // El objetivo activo es el ÚLTIMO creado sin alcanzar, no el primero — si el
  // paciente "cambia objetivo" sin haber llegado al anterior, el nuevo pasa a ser
  // el vigente (antes .find() devolvía siempre el más viejo y el cambio no se veía).
  const objetivoActivo = objetivos.filter(o => !o.alcanzado).at(-1)
  const logros = objetivos.filter(o => o.alcanzado)

  const direccionActiva = objetivoActivo && pesoPrimero != null ? direccionObjetivo(objetivoActivo.peso_objetivo, pesoPrimero) : null
  const porcentajeAvance = objetivoActivo && pesoActual != null && pesoPrimero != null
    ? Math.max(0, Math.min(100, direccionActiva === 'bajar'
        ? ((pesoPrimero - pesoActual) / (pesoPrimero - objetivoActivo.peso_objetivo)) * 100
        : ((pesoActual - pesoPrimero) / (objetivoActivo.peso_objetivo - pesoPrimero)) * 100))
    : 0
  const kgAvance = objetivoActivo && pesoActual != null && pesoPrimero != null
    ? +Math.abs(pesoActual - pesoPrimero).toFixed(1) : null

  const todasMedidas = registros
    .filter(r => r.tipo === 'medida')
    .flatMap(r => (medidasPorRegistro[r.id] || []).map(m => ({ ...m, fecha: r.fecha })))
    .sort(ordenarPorFechaYHora)
  const clavePartes = new Map()
  for (const m of todasMedidas) {
    const clave = m.parte_cuerpo === 'otro' ? m.parte_cuerpo_otro : m.parte_cuerpo
    const etiqueta = m.parte_cuerpo === 'otro' ? m.parte_cuerpo_otro : ETIQUETA_PARTE[m.parte_cuerpo]
    if (!clavePartes.has(clave)) clavePartes.set(clave, etiqueta)
  }
  const puntosParteSeleccionada = todasMedidas
    .filter(m => (m.parte_cuerpo === 'otro' ? m.parte_cuerpo_otro : m.parte_cuerpo) === parteSeleccionada)
    .map(m => ({ fecha: m.fecha, valor: m.medida_cm }))
  const acumuladoMedida = puntosParteSeleccionada.length > 1
    ? +(puntosParteSeleccionada.at(-1).valor - puntosParteSeleccionada[0].valor).toFixed(1)
    : null

  // Bitácora: un solo día puede tener hasta 3 registros (peso/medida/foto) — se agrupan en una tarjeta.
  const registrosPorFecha = new Map()
  for (const r of registros) {
    if (!registrosPorFecha.has(r.fecha)) registrosPorFecha.set(r.fecha, [])
    registrosPorFecha.get(r.fecha).push(r)
  }
  const fechasOrdenadas = [...registrosPorFecha.keys()].sort().reverse()

  return (
    <div className="progreso-page">
      <div className="page-header">
        <h1 className="page-titulo">Mi progreso</h1>
        <p className="page-subtitulo">Seguimiento de tu evolución</p>
      </div>

      <div className="progreso-contenido">
        <button className="progreso-btn-registro" onClick={() => { setErroresForm({}); setModalRegistro(true) }}>
          + Nuevo registro
        </button>

        {objetivoActivo ? (
          <div className="progreso-card">
            {pesoActual != null ? (
              <>
                <div className="progreso-pesos">
                  <div className="progreso-peso-item">
                    <span className="progreso-peso-valor">{pesoActual} kg</span>
                    <span className="progreso-peso-label">Actual</span>
                  </div>
                  <div className="progreso-flecha">→</div>
                  <div className="progreso-peso-item">
                    <span className="progreso-peso-valor progreso-meta">{objetivoActivo.peso_objetivo} kg</span>
                    <span className="progreso-peso-label">Meta</span>
                  </div>
                </div>

                <div className="progreso-avance-destacado">
                  <span className="progreso-porcentaje-grande">{Math.round(porcentajeAvance)}%</span>
                  {kgAvance != null && kgAvance > 0 && (
                    <span className="progreso-kg-avance">
                      {direccionActiva === 'bajar' ? `${kgAvance} kg perdidos` : `${kgAvance} kg ganados`}
                    </span>
                  )}
                </div>

                <div className="progreso-barra-wrap">
                  <div className="progreso-barra-fondo">
                    <div className="progreso-barra-fill" style={{ width: `${porcentajeAvance}%` }} />
                  </div>
                </div>
              </>
            ) : (
              <p className="progreso-sin-objetivo-texto">
                Meta: <strong>{objetivoActivo.peso_objetivo} kg</strong>. Cargá tu primer registro de peso para ver tu progreso.
              </p>
            )}

            <button className="progreso-btn-cambiar" onClick={() => {
              setFormObj({ peso_meta: '' })
              setErroresForm({})
              setPantallaObjetivo(true)
            }}>
              Cargar nuevo objetivo
            </button>
          </div>
        ) : (
          <div className="progreso-card">
            <p className="progreso-sin-objetivo-texto">Todavía no tenés un objetivo de peso activo.</p>
            <button className="btn-primario" onClick={() => { setFormObj({ peso_meta: '' }); setErroresForm({}); setPantallaObjetivo(true) }}>
              Cargar objetivo
            </button>
          </div>
        )}

        {logros.length > 0 && (
          <div className="progreso-card progreso-logros">
            <p className="progreso-seccion-label">🏆 Logros</p>
            {logros.map(l => (
              <p key={l.id} className="progreso-logro-item">
                Alcanzaste tu objetivo de <strong>{l.peso_objetivo} kg</strong> el {formatearFecha(l.fecha_alcanzado)}
              </p>
            ))}
          </div>
        )}

        {acumuladoPeso != null && (
          <div className="progreso-grafico-card">
            <p className="progreso-seccion-label">Curva de peso</p>
            <LineChart
              puntos={registrosPeso.map(r => ({ fecha: r.fecha, valor: r.peso }))}
              lineaReferencia={objetivoActivo?.peso_objetivo}
              etiquetaReferencia="Meta"
            />
            <div className="progreso-grafico-leyenda">
              <span><span className="leyenda-punto" />Real</span>
              {objetivoActivo && <span><span className="leyenda-linea lila" />Meta</span>}
            </div>
            <p className="progreso-calculo-nota" style={{ textAlign: 'center', marginTop: 6 }}>
              Cambio total: {acumuladoPeso > 0 ? '+' : ''}{acumuladoPeso} kg
            </p>
          </div>
        )}

        {clavePartes.size > 0 && (
          <div className="progreso-grafico-card">
            <p className="progreso-seccion-label">Medidas</p>
            <select className="form-input" value={parteSeleccionada} onChange={e => setParteSeleccionada(e.target.value)}>
              {[...clavePartes.entries()].map(([clave, etiqueta]) => (
                <option key={clave} value={clave}>{etiqueta}</option>
              ))}
            </select>
            {puntosParteSeleccionada.length >= 2 ? (
              <>
                <LineChart puntos={puntosParteSeleccionada} />
                {acumuladoMedida != null && (
                  <p className="progreso-calculo-nota" style={{ textAlign: 'center', marginTop: 6 }}>
                    Cambio total: {acumuladoMedida > 0 ? '+' : ''}{acumuladoMedida} cm
                  </p>
                )}
              </>
            ) : (
              <p className="progreso-calculo-nota" style={{ textAlign: 'center', marginTop: 10 }}>
                Necesitás al menos 2 registros de esta medida para ver el gráfico.
              </p>
            )}
          </div>
        )}

        {fechasOrdenadas.length > 0 && (
          <div className="progreso-bitacora">
            <p className="progreso-seccion-label">Bitácora</p>
            {fechasOrdenadas.map(fecha => {
              const grupo = registrosPorFecha.get(fecha)
              const regPeso = grupo.find(r => r.tipo === 'peso')
              const medidas = grupo.filter(r => r.tipo === 'medida').flatMap(r => medidasPorRegistro[r.id] || [])
              const fotos = grupo.filter(r => r.tipo === 'foto').flatMap(r => fotosPorRegistro[r.id] || [])
              const notas = grupo.map(r => r.notas).find(Boolean)
              return (
                <div key={fecha} className="progreso-entrada">
                  <div className="progreso-entrada-header">
                    <span className="progreso-entrada-fecha">{formatearFecha(fecha)}</span>
                    {regPeso && <span className="progreso-entrada-peso">{regPeso.peso} kg</span>}
                  </div>
                  {medidas.length > 0 && (
                    <div className="progreso-medidas">
                      {medidas.map(m => (
                        <span key={m.id}>
                          {m.parte_cuerpo === 'otro' ? m.parte_cuerpo_otro : ETIQUETA_PARTE[m.parte_cuerpo]}: {m.medida_cm} cm
                        </span>
                      ))}
                    </div>
                  )}
                  {notas && <p className="progreso-entrada-notas">{notas}</p>}
                  {fotos.length > 0 && (
                    <div className="progreso-entrada-fotos">
                      {fotos.map(f => (
                        <img key={f.id} src={f.foto_url} alt="Registro" className="progreso-entrada-foto" />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* MODAL OBJETIVO */}
      {pantallaObjetivo && (
        <div className="modal-overlay" onClick={() => setPantallaObjetivo(false)}>
          <div className="modal-card modal-card-scroll" onClick={e => e.stopPropagation()}>
            <h3 className="modal-titulo">Configurar objetivo</h3>
            <div className="progreso-form">
              <label className="form-label">Peso meta (kg)</label>
              <input className="form-input" type="number" step="0.1" placeholder="Ej: 65.0"
                value={formObj.peso_meta} onChange={e => setFormObj(f => ({ ...f, peso_meta: e.target.value }))} />
              {erroresForm.peso_meta && <p className="progreso-error">{erroresForm.peso_meta}</p>}
              {erroresForm.general && <p className="progreso-error">{erroresForm.general}</p>}
            </div>
            <div className="modal-actions">
              <button className="btn-secundario" onClick={() => setPantallaObjetivo(false)}>Cancelar</button>
              <button className="btn-primario" onClick={guardarObjetivo}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL REGISTRO */}
      {modalRegistro && (
        <div className="modal-overlay" onClick={() => setModalRegistro(false)}>
          <div className="modal-card modal-card-scroll" onClick={e => e.stopPropagation()}>
            <h3 className="modal-titulo">Nuevo registro</h3>
            <div className="progreso-form">
              <label className="form-label">Fecha</label>
              <input className="form-input" type="date"
                value={formReg.fecha} onChange={e => setFormReg(f => ({ ...f, fecha: e.target.value }))} />

              <label className="form-label">Peso (kg) — opcional</label>
              <input className="form-input" type="number" step="0.1" placeholder="Ej: 71.2"
                value={formReg.peso} onChange={e => setFormReg(f => ({ ...f, peso: e.target.value }))} />
              {erroresForm.peso && <p className="progreso-error">{erroresForm.peso}</p>}

              <p className="form-seccion-titulo">Medidas opcionales (cm)</p>
              <div className="form-grid-2">
                {PARTES_CUERPO.map(parte => (
                  <div key={parte}>
                    <label className="form-label">{ETIQUETA_PARTE[parte]}</label>
                    <input className="form-input" type="number" step="0.5" placeholder="—"
                      value={formReg.medidas[parte]}
                      onChange={e => setFormReg(f => ({ ...f, medidas: { ...f.medidas, [parte]: e.target.value } }))} />
                    {erroresForm[`medida_${parte}`] && <p className="progreso-error">{erroresForm[`medida_${parte}`]}</p>}
                  </div>
                ))}
              </div>

              <label className="form-label">Otra medida — opcional</label>
              <div className="form-grid-2">
                <input className="form-input" type="text" placeholder="¿Qué parte?"
                  value={formReg.otroNombre} onChange={e => setFormReg(f => ({ ...f, otroNombre: e.target.value }))} />
                <input className="form-input" type="number" step="0.5" placeholder="cm"
                  value={formReg.otroValor} onChange={e => setFormReg(f => ({ ...f, otroValor: e.target.value }))} />
              </div>
              {erroresForm.otroNombre && <p className="progreso-error">{erroresForm.otroNombre}</p>}
              {erroresForm.otro && <p className="progreso-error">{erroresForm.otro}</p>}

              <label className="form-label">Fotos — opcional (hasta {MAX_FOTOS})</label>
              <button className="form-btn-foto" onClick={() => inputFotoRef.current?.click()}>
                {fotosSeleccionadas.length > 0 ? `📷 ${fotosSeleccionadas.length} foto(s) elegida(s)` : '📷 Elegir fotos'}
              </button>
              <input ref={inputFotoRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={e => setFotosSeleccionadas(Array.from(e.target.files).slice(0, MAX_FOTOS))} />

              <label className="form-label">Notas</label>
              <textarea className="form-input" rows={3} placeholder="¿Cómo te sentís hoy?"
                value={formReg.notas} onChange={e => setFormReg(f => ({ ...f, notas: e.target.value }))} />

              {erroresForm.general && <p className="progreso-error">{erroresForm.general}</p>}
            </div>
            <div className="modal-actions">
              <button className="btn-secundario" onClick={() => setModalRegistro(false)}>Cancelar</button>
              <button className="btn-primario" onClick={guardarRegistro} disabled={subiendo}>
                {subiendo ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
